// src/components/managed-sources/KlaviyoAuthStep.tsx

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconMail, IconPlugConnected, IconPlus, IconX } from "@tabler/icons-react";

import { listAuthorizations } from "@/api/authorizations";
import type { AuthorizationDTO } from "@/types/authorizations";
import { KlaviyoConnectionForm } from "@/components/authorizations/KlaviyoConnectionForm";
import { useBranding } from "@/context/BrandingContext";

type Props = {
  authorizationId: string | null;
  onAuthorizationChange: (id: string | null) => void;
  onValidated: (valid: boolean) => void;
};

export default function KlaviyoAuthStep({
  authorizationId,
  onAuthorizationChange,
  onValidated,
}: Props) {
  const { branding } = useBranding();
  const [existingAuths, setExistingAuths] = useState<AuthorizationDTO[]>([]);
  const [loadingAuths, setLoadingAuths] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  async function loadAuths(autoSelectLatest: boolean = false) {
    setLoadingAuths(true);
    setLoadError("");
    try {
      const auths = await listAuthorizations({ type: "KLAVIYO" });
      setExistingAuths(auths);
      if (autoSelectLatest && auths.length > 0) {
        const latest = [...auths].sort((a, b) =>
          (b.created_at || "").localeCompare(a.created_at || ""),
        )[0];
        onAuthorizationChange(latest.id);
      }
    } catch (e: any) {
      setLoadError(e?.message || "Failed to load Klaviyo connections");
    } finally {
      setLoadingAuths(false);
    }
  }

  useEffect(() => {
    loadAuths();
  }, []);

  useEffect(() => {
    onValidated(!!authorizationId);
  }, [authorizationId, onValidated]);

  const selectOptions = existingAuths.map((a) => ({ value: a.id, label: a.name }));

  return (
    <Stack gap="lg">
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="lime">
          <IconMail size={26} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Connect Klaviyo
          </Text>
          <Text size="sm" c="dimmed">
            Pick the Klaviyo connection {branding.productName} should pull email
            activity from.
          </Text>
        </Stack>
      </Group>

      {loadingAuths ? (
        <Stack align="center" justify="center" py={40}>
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading Klaviyo connections...
          </Text>
        </Stack>
      ) : loadError ? (
        <Alert color="red" icon={<IconX size={16} />}>
          {loadError}
        </Alert>
      ) : existingAuths.length === 0 || createOpen ? (
        // Inline, in the wizard's own modal: a drawer opened from here would
        // sit under the modal's overlay and focus trap.
        <KlaviyoConnectionForm
          onCreated={() => {
            setCreateOpen(false);
            loadAuths(true);
          }}
          onCancel={
            existingAuths.length > 0 ? () => setCreateOpen(false) : undefined
          }
        />
      ) : (
        <Stack gap="md">
          <Select
            label="Klaviyo connection"
            placeholder="Select a saved connection"
            data={selectOptions}
            value={authorizationId}
            onChange={onAuthorizationChange}
            size="md"
          />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Need a different account?
            </Text>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => setCreateOpen(true)}
            >
              Add another Klaviyo connection
            </Button>
          </Group>
        </Stack>
      )}

      {authorizationId && !createOpen && (
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderColor: "var(--mantine-color-green-4)",
            backgroundColor: "var(--mantine-color-green-0)",
          }}
        >
          <Group gap="sm">
            <ThemeIcon size={32} color="green" variant="light" radius="xl">
              <IconPlugConnected size={16} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Connected
              </Text>
              <Text size="xs" c="dimmed">
                {existingAuths.find((a) => a.id === authorizationId)?.name ||
                  "Klaviyo connection ready"}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
